import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

import { EMPTY, mergeMap, Observable, of } from 'rxjs';

import { BaasConfirm } from './baas-confirm';
import { BAAS_CONFIRM_SETTINGS } from './baas-confirm.tokens';
import { BaasConfirmOptions, BaasConfirmSettings } from './baas-confirm.types';

/** Confirmation dialog service. */
@Injectable({ providedIn: 'root' })
export class BaasConfirmService {
    readonly #dialog = inject(MatDialog);
    readonly #settings = inject<BaasConfirmSettings>(BAAS_CONFIRM_SETTINGS, { optional: true });

    /**
     * Opens a confirm dialog.
     * @param data Data needed for the dialog.
     */
    /**
     * Opens a confirmation dialog with the specified options.
     *
     * @param data - The configuration options for the confirmation dialog
     * @returns An Observable that emits `true` if the user confirms, `false` if the user cancels, or `undefined` if the dialog is dismissed without a clear action
     */
    open(data: BaasConfirmOptions): Observable<boolean | undefined> {
        return this.#handleConfirmation(data);
    }

    /**
     * Opens a confirm dialog and triggers when confirmed.
     * @param data
     */
    confirm(data: BaasConfirmOptions): Observable<void> {
        return this.#handleConfirmation(data).pipe(
            mergeMap((result) => {
                if (result === true) return of(undefined);
                else return EMPTY;
            })
        );
    }

    /**
     * Opens a confirm dialog and triggers when refuted (not confirmed).
     * @param data
     */
    refute(data: BaasConfirmOptions): Observable<void> {
        return this.#handleConfirmation(data).pipe(
            mergeMap((result) => {
                if (result === false) return of(undefined);
                else return EMPTY;
            })
        );
    }

    #handleConfirmation(data: BaasConfirmOptions): Observable<boolean | undefined> {
        if (!data) throw new Error('Data is required for the confirmation dialog.');
        // by default this must be true
        if (data.refutable === undefined) data.refutable = true;
        // by default allow closing the dialog
        if (data.closeable == null) data.closeable = true;

        const config: MatDialogConfig<BaasConfirmOptions> = {
            height: data.height || (this.#settings?.height == null || data.ignoreGlobalSettings ? '190px' : this.#settings.height),
            width: data.width || (this.#settings?.width == null || data.ignoreGlobalSettings ? '450px' : this.#settings.width),
            data: data
        };

        config.disableClose = data.closeable === false || data.closeable === 'iconOnly';

        const dialogRef = this.#dialog.open(BaasConfirm, config);

        return dialogRef.afterClosed();
    }
}
